import { useState, type ReactNode } from "react";
import {
  DRAWN_CEILING_RANGE_M,
  addDrawnStorey,
  copyDrawnStorey,
  drawnRoomAreaM2,
  drawnRoomWalls,
  drawnStoreys,
  nextDrawnStorey,
  removeDrawnStorey,
  rotateDrawnRoom,
  setDrawnWallLength,
  snapDrawnRoom,
  splitDrawnRoom,
  storyLabel,
  type DrawnPlan,
  type DrawnRoom,
} from "@sts/geometry";
import { nextRoomId, type DrawnPlanState } from "./use-drawn-plan.js";

/**
 * Drawing the floor plan by hand, where there was no scan (migration 0047).
 *
 * WHAT THIS PANEL IS FOR, and what the plan itself is for. A room's PLACE is a
 * gesture — drag it out on the grid, and it lands on its neighbours' walls.
 * Everything else about a room is a number, and numbers are typed: its name,
 * each wall's length in millimetres, the floor-to-ceiling height. That is how
 * both competing tools do it, and it is the only way a dimension is ever
 * exact. So the plan gets the drag and this panel gets the rest.
 *
 * MILLIMETRES HERE, METRES ON THE WIRE. A surveyor writes 4200, not 4.2. The
 * conversion happens at this boundary and nowhere else: everything stored,
 * sent and drawn is metres, as every other plan geometry is.
 *
 * NOTHING IS GUESSED. A room needs a name and a floor-to-ceiling height before
 * it can be added, because the height is the room's volume and so part of its
 * heat loss. The form will not add a room without one, and the API refuses one
 * too — the same rule, `validateDrawnPlan`, on both sides.
 */

export interface DrawPlanPanelProps {
  readonly state: DrawnPlanState;
  /** The storey the plan is showing; rooms are added to this one. */
  readonly storey: number;
  readonly onStoreyChange: (storey: number) => void;
  readonly canEdit: boolean;
  /**
   * The floor-to-ceiling height in hand, millimetres as typed, held outside
   * the panel so the plan's own field and this one are the same figure: a
   * surveyor who typed the height to drag a room out does not type it again
   * to add one by hand. Left out, the panel keeps its own.
   */
  readonly heightMm?: string;
  readonly onHeightMmChange?: (value: string) => void;
}

const mm = (metres: number): number => Math.round(metres * 1000);
const metres = (millimetres: number): number => millimetres / 1000;

/** A typed millimetre figure, or undefined when it is not a number at all. */
export function readMm(value: string): number | undefined {
  const n = Number(value.trim());
  return Number.isFinite(n) && n > 0 ? metres(n) : undefined;
}

export function DrawPlanPanel({
  state,
  storey,
  onStoreyChange,
  canEdit,
  heightMm,
  onHeightMmChange,
}: DrawPlanPanelProps): ReactNode {
  const plan = state.plan;
  const storeys = drawnStoreys(plan);
  const rooms = plan.rooms.filter((r) => r.story === storey);
  const [name, setName] = useState("");
  const [width, setWidth] = useState("");
  const [depth, setDepth] = useState("");
  const [ownHeight, setOwnHeight] = useState("");
  const height = heightMm ?? ownHeight;
  const setHeight = onHeightMmChange ?? setOwnHeight;
  const [formError, setFormError] = useState<string | null>(null);

  const replace = (rooms: readonly DrawnRoom[]): void => {
    state.update({ ...plan, rooms });
  };
  const replaceRoom = (id: string, next: DrawnRoom): void =>
    replace(plan.rooms.map((r) => (r.id === id ? next : r)));

  function addRoom(): void {
    const widthM = readMm(width);
    const depthM = readMm(depth);
    const ceilingHeightM = readMm(height);
    if (!name.trim()) {
      setFormError("Give the room a name, so the heat loss can say which room it is.");
      return;
    }
    if (widthM === undefined || depthM === undefined) {
      setFormError("Give the room a width and a depth in millimetres.");
      return;
    }
    if (
      ceilingHeightM === undefined ||
      ceilingHeightM < DRAWN_CEILING_RANGE_M.min ||
      ceilingHeightM > DRAWN_CEILING_RANGE_M.max
    ) {
      setFormError(
        `Give the room a floor-to-ceiling height between ${mm(DRAWN_CEILING_RANGE_M.min)} and ` +
          `${mm(DRAWN_CEILING_RANGE_M.max)} mm. It is the room's volume, so nothing here guesses it.`,
      );
      return;
    }
    setFormError(null);
    // Placed clear to the right of everything already on this storey, then
    // snapped: a new room never lands on top of one already drawn.
    const right = rooms.length === 0 ? 0 : Math.max(...rooms.map((r) => r.centre.x + r.widthM / 2));
    const room: DrawnRoom = {
      id: nextRoomId(plan),
      name: name.trim(),
      story: storey,
      centre: { x: right + widthM / 2, y: depthM / 2 },
      widthM,
      depthM,
      rotationDeg: 0,
      ceilingHeightM,
    };
    replace([...plan.rooms, snapDrawnRoom(room, plan.rooms).room]);
    setName("");
    setWidth("");
    setDepth("");
  }

  return (
    <section className="draw-plan" aria-label="Draw the plan" data-testid="draw-plan-panel">
      <header className="draw-plan__header">
        <h3>Rooms on this storey</h3>
        <p className="muted">
          Drawn by hand, not scanned. Every dimension here is one you have entered.
        </p>
      </header>

      {state.error ? (
        <p className="error" role="alert" data-testid="draw-plan-error">
          {state.error}{" "}
          <button type="button" className="link" onClick={state.dismissError}>
            Dismiss
          </button>
        </p>
      ) : null}
      {state.note ? (
        <p className="notice" role="status" data-testid="draw-plan-note">
          {state.note}{" "}
          <button type="button" className="link" onClick={state.dismissNote}>
            Dismiss
          </button>
        </p>
      ) : null}

      <div className="draw-plan__storeys">
        <label>
          Storey
          <select
            value={storey}
            onChange={(event) => onStoreyChange(Number(event.target.value))}
          >
            {storeys.map((s) => (
              <option key={s.story} value={s.story}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
        {canEdit ? (
          <>
            <button
              type="button"
              onClick={() => {
                const next = nextDrawnStorey(plan, "up");
                state.update(addDrawnStorey(plan, next));
                onStoreyChange(next);
              }}
            >
              Add a storey above
            </button>
            <button
              type="button"
              onClick={() => {
                const next = nextDrawnStorey(plan, "down");
                state.update(addDrawnStorey(plan, next));
                onStoreyChange(next);
              }}
            >
              Add a storey below
            </button>
            <button
              type="button"
              disabled={rooms.length === 0}
              title="Put the same rooms on the storey above, to change from there"
              onClick={() => {
                const above = storey + 1;
                // Each copy gets an id of its own, and one already handed out
                // in this same copy is not handed out again.
                const minted = new Set<string>();
                const copied = copyDrawnStorey(plan, storey, above, () => {
                  const id = nextRoomId(plan, minted);
                  minted.add(id);
                  return id;
                });
                state.update(copied);
                onStoreyChange(above);
              }}
            >
              Copy this storey up
            </button>
            <button
              type="button"
              disabled={rooms.length > 0 || storeys.length < 2}
              title={
                rooms.length > 0
                  ? "Delete this storey's rooms first"
                  : "Remove this empty storey"
              }
              onClick={() => {
                const remaining = storeys.filter((s) => s.story !== storey);
                state.update(removeDrawnStorey(plan, storey));
                if (remaining[0]) onStoreyChange(remaining[0].story);
              }}
            >
              Delete this storey
            </button>
          </>
        ) : null}
      </div>

      {canEdit ? (
        <form
          className="draw-plan__add"
          aria-label="Add a room"
          onSubmit={(event) => {
            event.preventDefault();
            addRoom();
          }}
        >
          <label>
            Room name
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label>
            Width (mm)
            <input inputMode="numeric" value={width} onChange={(e) => setWidth(e.target.value)} />
          </label>
          <label>
            Depth (mm)
            <input inputMode="numeric" value={depth} onChange={(e) => setDepth(e.target.value)} />
          </label>
          <label>
            Floor to ceiling (mm)
            <input inputMode="numeric" value={height} onChange={(e) => setHeight(e.target.value)} />
          </label>
          <button type="submit">Add room</button>
          {formError ? (
            <p className="error" role="alert" data-testid="draw-plan-form-error">
              {formError}
            </p>
          ) : null}
          <p className="muted">
            Or press <strong>Draw a room</strong> above the plan and drag out a
            rectangle. A room lands on the walls of the rooms beside it.
          </p>
        </form>
      ) : null}

      {rooms.length === 0 ? (
        <p className="empty">
          Nothing has been drawn on {storyLabel(storey)} yet.
        </p>
      ) : (
        <ul className="draw-plan__rooms">
          {rooms.map((room) => (
            <DrawnRoomRow
              key={room.id}
              room={room}
              canEdit={canEdit}
              onChange={(next) => replaceRoom(room.id, next)}
              onDelete={() => replace(plan.rooms.filter((r) => r.id !== room.id))}
              onSplit={(axis) => {
                // Down the middle: the two halves are then sized by typing
                // their wall lengths, which is exact where a dragged cut is not.
                const span = axis === "width" ? room.widthM : room.depthM;
                const pair = splitDrawnRoom(
                  room,
                  axis,
                  span / 2,
                  nextRoomId(plan),
                  `${room.name} 2`,
                );
                if (!pair) return;
                replace(plan.rooms.flatMap((r) => (r.id === room.id ? [...pair] : [r])));
              }}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

/** One room: its name, its four wall lengths, and what can be done to it. */
function DrawnRoomRow({
  room,
  canEdit,
  onChange,
  onDelete,
  onSplit,
}: {
  readonly room: DrawnRoom;
  readonly canEdit: boolean;
  readonly onChange: (next: DrawnRoom) => void;
  readonly onDelete: () => void;
  readonly onSplit: (axis: "width" | "depth") => void;
}): ReactNode {
  const walls = drawnRoomWalls(room);
  const [lengths, setLengths] = useState<Record<number, string>>({});
  const [name, setName] = useState(room.name);

  return (
    <li className="draw-plan__room" data-testid={`drawn-room-${room.id}`}>
      <div className="draw-plan__room-head">
        {canEdit ? (
          <label>
            Room name
            <input
              value={name}
              aria-label={`Name of ${room.name}`}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => {
                const trimmed = name.trim();
                if (trimmed && trimmed !== room.name) onChange({ ...room, name: trimmed });
                else setName(room.name);
              }}
            />
          </label>
        ) : (
          <strong>{room.name}</strong>
        )}
        <span className="muted">
          {drawnRoomAreaM2(room).toFixed(2)} m² · {mm(room.ceilingHeightM)} mm to the ceiling
        </span>
      </div>

      {canEdit ? (
        <>
          <div className="draw-plan__walls">
            {walls.map((wall) => (
              <div key={wall.index} className="draw-plan__wall">
                <label>
                  {`Wall ${wall.index + 1} length (mm)`}
                  <input
                    inputMode="numeric"
                    aria-label={`Wall ${wall.index + 1} of ${room.name}, length in millimetres`}
                    value={lengths[wall.index] ?? String(mm(wall.lengthM))}
                    onChange={(e) =>
                      setLengths((held) => ({ ...held, [wall.index]: e.target.value }))
                    }
                  />
                </label>
                <button
                  type="button"
                  onClick={() => {
                    const typed = readMm(lengths[wall.index] ?? String(mm(wall.lengthM)));
                    if (typed === undefined) return;
                    onChange(setDrawnWallLength(room, wall.index, typed));
                    setLengths((held) => {
                      const { [wall.index]: _used, ...rest } = held;
                      return rest;
                    });
                  }}
                >
                  {`Apply wall ${wall.index + 1}`}
                </button>
              </div>
            ))}
          </div>

          <div className="draw-plan__room-actions">
            <label>
              Floor to ceiling (mm)
              <input
                inputMode="numeric"
                aria-label={`Floor to ceiling height of ${room.name}, in millimetres`}
                defaultValue={String(mm(room.ceilingHeightM))}
                onBlur={(e) => {
                  const typed = readMm(e.target.value);
                  if (typed !== undefined && typed !== room.ceilingHeightM) {
                    onChange({ ...room, ceilingHeightM: typed });
                  }
                }}
              />
            </label>
            <button type="button" onClick={() => onChange(rotateDrawnRoom(room))}>
              {`Rotate ${room.name}`}
            </button>
            <button type="button" onClick={() => onSplit("width")}>
              {`Split ${room.name} across`}
            </button>
            <button type="button" onClick={() => onSplit("depth")}>
              {`Split ${room.name} down`}
            </button>
            <button type="button" className="danger" onClick={onDelete}>
              {`Delete ${room.name}`}
            </button>
          </div>
        </>
      ) : null}
    </li>
  );
}
